import React, { createRef, memo, useEffect } from 'react';
import Button from '@atlaskit/button';
import DropdownMenu, {
	DropdownItemGroup,
	DropdownItem,
	type CustomTriggerProps,
} from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import {
	type IssueContainerName,
	PROJECT_ISSUE_CONTAINER_NAME,
	SERVICE_DESK_ISSUE_CONTAINER_NAME,
} from '@atlassian/jira-common-constants/src/issue-container-names';
import {
	type ProjectType,
	SOFTWARE_PROJECT,
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types';
import { useIntl } from '@atlassian/jira-intl';
import useMergeRefs from '@atlassian/jira-merge-refs';
import { useIsPremiumUserSeat } from '@atlassian/jira-router-resources-business-navigation';
import type { RefObject } from '@atlassian/jira-shared-types/src/general.tsx';
import ArchiveTrigger from './archive-trigger';
import messages from './messages';

export type Props = {
	issueContainerName: IssueContainerName;
	projectType: ProjectType;
	onTrash: () => void;
	onDelete: () => void;
	setDropDownMenuButtonRef: (dropDownMenuRef: RefObject) => void;
};

export const defaultProps = {
	issueContainerName: PROJECT_ISSUE_CONTAINER_NAME,
} as const;

export const ProjectDetailsMenu = ({
	onDelete,
	onTrash,
	issueContainerName,
	projectType,
	setDropDownMenuButtonRef,
}: Props) => {
	const { formatMessage } = useIntl();
	const isCorePremiumUserSeat = useIsPremiumUserSeat();

	const showTrashTrigger = () => {
		switch (projectType) {
			case SOFTWARE_PROJECT:
			case CORE_PROJECT:
			case PRODUCT_DISCOVERY_PROJECT:
				return true;
			case SERVICE_DESK_PROJECT:
				return true;
			default:
				return false;
		}
	};

	const deleteMessage = formatMessage(
		issueContainerName === SERVICE_DESK_ISSUE_CONTAINER_NAME
			? messages.actionDeleteServiceProject
			: messages.actionDeleteProject,
	);

	const dropDownMenuButtonRef = createRef<HTMLButtonElement>();

	useEffect(() => {
		if (dropDownMenuButtonRef) {
			setDropDownMenuButtonRef(dropDownMenuButtonRef);
		}
	}, [setDropDownMenuButtonRef, dropDownMenuButtonRef]);

	return (
		<DropdownMenu
			testId="project-details.header.menu.dropdown-menu"
			// eslint-disable-next-line jira/react-no-inline-function-prop
			trigger={({ triggerRef, ...props }: CustomTriggerProps) => (
				<Button
					{...props}
					appearance="subtle"
					iconBefore={<MoreIcon label={formatMessage(messages.menuLabel)} />}
					ref={
						// eslint-disable-next-line react-hooks/rules-of-hooks
						useMergeRefs(triggerRef, dropDownMenuButtonRef)
					}
				/>
			)}
			placement="bottom-end"
			shouldRenderToParent
		>
			<DropdownItemGroup>
				{showTrashTrigger() ? (
					<DropdownItem onClick={onTrash}>
						{formatMessage(messages.actionTrashProject)}
					</DropdownItem>
				) : (
					<DropdownItem onClick={onDelete}>{deleteMessage}</DropdownItem>
				)}
				<ArchiveTrigger
					shouldHideFeatureGateTrigger
					isCorePremiumUserSeat={isCorePremiumUserSeat}
				/>
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

export default memo(ProjectDetailsMenu);
